

import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { useEvents } from "@/hooks/use-events"
import { useVijayawadaLayers } from "@/hooks/use-vijayawada-layers"
import { usePondingLayer } from "@/hooks/use-ponding-layer"
import { useCFDLayers } from "@/hooks/use-cfd-layers"
import { CFDSimulationPanel } from "@/components/cfd-simulation"
import {
  Droplets,
  ChevronRight,
  ChevronLeft,
  Filter,
  X,
  Layers,
  Cpu,
} from "lucide-react"

export function MapFilterPanel() {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [expandedSections, setExpandedSections] = useState<string[]>([])
  const { events, loading: eventsLoading } = useEvents()

  // Use layer-specific hooks that manage their own registration
  const vijayawada = useVijayawadaLayers()
  const ponding = usePondingLayer()
  const cfd = useCFDLayers()

  const handleSimulationComplete = (result: any) => {
    console.log('Simulation complete:', result)
    // TODO: Load result GeoTIFF on map
  }

  const handleSimulationError = (error: string) => {
    console.error('Simulation error:', error)
  }

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => (prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]))
  }

  return (
    <div className={`${isCollapsed ? 'w-0' : 'flex'}`}>
      {/* Sidebar panel */}
      <aside
        className={`w-[420px] h-full border-r bg-sidebar/50 supports-[backdrop-filter]:bg-sidebar/50 transition-transform duration-300 ease-in-out z-[9998] flex flex-col ${isCollapsed ? '-translate-x-full' : 'translate-x-0'
          }`}
      >
        {/* Fixed header */}
        <div className="flex-shrink-0 p-2 space-y-4">
          <div className="flex pl-4">
            <h2 className="text-sm font-semibold">Filters</h2>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
          <div className="p-4 space-y-4" style={{ marginBottom: 40 }}>

            {/* Vijayawada Event Layers */}
            {vijayawada.layers.length > 0 && (
              <Card className="shadow-md">
                <CardHeader className="pb-3">
                  <Button
                    variant="ghost"
                    className="w-full justify-between p-0 h-auto"
                    onClick={() => toggleSection("vijayawada-layers")}
                  >
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      <span className="font-medium">Vijayawada Event Layers</span>
                    </div>
                    <ChevronRight className={`h-4 w-4 transition-transform duration-200 ease-in-out ${expandedSections.includes("vijayawada-layers") ? "rotate-90" : "rotate-0"
                      }`} />
                  </Button>
                </CardHeader>

                {expandedSections.includes("vijayawada-layers") && (
                  <CardContent className="pt-0 space-y-2 max-h-[300px] overflow-y-auto">
                    {vijayawada.layers.map((layer) => (
                      <div key={layer.id} className="border rounded p-2">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1">
                            <span className="text-xs font-medium truncate block" title={layer.name}>
                              {layer.name}
                            </span>
                            {layer.metadata?.vmin !== undefined && layer.metadata?.vmax !== undefined && (
                              <span className="text-xs text-muted-foreground block">
                                Range: {layer.metadata.vmin} to {layer.metadata.vmax}
                              </span>
                            )}
                            {layer.tileConfig?.bounds && (
                              <span className="text-xs text-muted-foreground block">
                                📍 {layer.tileConfig.bounds.getSouth().toFixed(2)},{layer.tileConfig.bounds.getWest().toFixed(2)}
                              </span>
                            )}
                          </div>
                          <Switch
                            checked={layer.visible}
                            onCheckedChange={() => vijayawada.toggleLayer(layer.id)}
                          />
                        </div>
                        {layer.visible && (
                          <div>
                            <label className="text-xs text-muted-foreground">
                              Opacity: {Math.round((layer.opacity ?? 0.7) * 100)}%
                            </label>
                            <Slider
                              value={[layer.opacity ?? 0.7]}
                              onValueChange={([value]) => vijayawada.setOpacity(layer.id, value)}
                              max={1}
                              min={0.1}
                              step={0.1}
                              className="w-full"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            )}

            {/* Ponding Logs */}
            <Card className="shadow-md">
              <CardHeader className="pb-3">
                <Button
                  variant="ghost"
                  className="w-full justify-between p-0 h-auto"
                  onClick={() => toggleSection("ponding-logs")}
                >
                  <div className="flex items-center gap-2">
                    <Droplets className="h-4 w-4" />
                    <span className="font-medium">Ponding Logs</span>
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ease-in-out ${expandedSections.includes("ponding-logs") ? "rotate-90" : "rotate-0"
                    }`} />
                </Button>
              </CardHeader>

              {expandedSections.includes("ponding-logs") && (
                <CardContent className="pt-0 space-y-3 max-h-[300px] ">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Droplets className="h-4 w-4 text-blue-500" />
                      <span className="text-sm">Show Ponding Events</span>
                    </div>
                    <Switch checked={ponding.layer?.visible ?? true} onCheckedChange={ponding.toggleVisibility} />
                  </div>

                  {/* Event Selector */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Filter by Event</Label>
                    {eventsLoading ? (
                      <div className="text-xs text-muted-foreground">Loading events...</div>
                    ) : events.length === 0 ? (
                      <div className="text-xs text-muted-foreground">No events found</div>
                    ) : (
                      <div className="space-y-1">
                        <Button
                          variant={ponding.selectedEventId === null ? "default" : "outline"}
                          size="sm"
                          className="w-full justify-start text-xs h-8"
                          onClick={() => ponding.setEventFilter(null)}
                        >
                          All Events
                        </Button>
                        {events.map((event) => (
                          <div key={event.id} className="flex items-center gap-1">
                            <Button
                              variant={ponding.selectedEventId === event.id ? "default" : "outline"}
                              size="sm"
                              className="flex-1 justify-start text-xs h-8"
                              onClick={() => ponding.setEventFilter(event.id)}
                            >
                              <span className="truncate">{event.title}</span>
                            </Button>
                            {ponding.selectedEventId === event.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => ponding.setEventFilter(null)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground px-1">
                    Display reported ponding locations on the map with depth indicators
                  </div>
                </CardContent>
              )}
            </Card>

            {/* CFD Simulation */}
            <Card className="shadow-md">
              <CardHeader className="pb-3">
                <Button
                  variant="ghost"
                  className="w-full justify-between p-0 h-auto"
                  onClick={() => toggleSection("cfd-simulation")}
                >
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4" />
                    <span className="font-medium">CFD Simulation</span>
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ease-in-out ${expandedSections.includes("cfd-simulation") ? "rotate-90" : "rotate-0"
                    }`} />
                </Button>
              </CardHeader>

              {expandedSections.includes("cfd-simulation") && (
                <CardContent className="pt-0 space-y-3">
                  {/* CFD Layers Control */}
                  {cfd.layers.length > 0 && (
                    <div className="border-b pb-3 space-y-2">
                      <Label className="text-xs text-muted-foreground">Active Layers</Label>
                      {cfd.layers.map((layer) => (
                        <div key={layer.id} className="border rounded p-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium truncate" title={layer.name}>
                              {layer.name}
                            </span>
                            <Switch
                              checked={layer.visible}
                              onCheckedChange={() => cfd.toggleLayer(layer.id)}
                            />
                          </div>
                          {layer.visible && (
                            <div>
                              <label className="text-xs text-muted-foreground">
                                Opacity: {Math.round((layer.opacity ?? 0.7) * 100)}%
                              </label>
                              <Slider
                                value={[layer.opacity ?? 0.7]}
                                onValueChange={([value]) => cfd.setOpacity(layer.id, value)}
                                max={1}
                                min={0.1}
                                step={0.1}
                                className="w-full"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Simulation Panel */}
                  <CFDSimulationPanel
                    onSimulationComplete={handleSimulationComplete}
                    onError={handleSimulationError}
                  />
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </aside>

      {/* Toggle button - positioned at screen edge when collapsed */}
      <div className={`${isCollapsed ? 'fixed left-0 top-20 z-[9999]' : 'flex-shrink-0'}`}>
        {isCollapsed ? (
          <Button
            variant="default"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-r-lg rounded-l-none border-l-0"
          >
            <ChevronRight className="h-4 w-4 mr-1 transition-transform duration-200 ease-in-out" />
            <Filter className="h-4 w-4 transition-opacity duration-200 ease-in-out" />
          </Button>
        ) : (
          <div className="h-full flex items-start pt-4">
            <Button
              variant="default"
              size="sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-r-lg rounded-l-none border-l-0"
            >
              <ChevronLeft className="h-4 w-4 transition-transform duration-200 ease-in-out" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
